import { CustomError } from '../../errors';
import { BudgetFieldsFragment, BudgetPurpose } from '../../graphql/generated';
import { DateRange } from '../../types';
import { getTotalMonths } from '../../util/dateTime';
import { useCurrentUser } from '../user';

export const BUDGET_PURPOSES: BudgetPurpose[] = [
    'income',
    'fixed',
    'lifestyle',
    'saving_investing',
];

export const BUDGET_PURPOSE_OPTIONS: { label: string; value: BudgetPurpose }[] =
    [
        {
            label: 'Fixed',
            value: 'fixed',
        },
        {
            label: 'Lifestyle',
            value: 'lifestyle',
        },
    ];

export const isSpendingBudgetPurpose = (
    purpose: BudgetPurpose | null | undefined
) => purpose === 'fixed' || purpose === 'lifestyle';

export const getBudgetPurposeLabel = (purpose: BudgetPurpose) =>
    ({
        fixed: 'Fixed',
        lifestyle: 'Lifestyle',
        saving_investing: 'Saving & Investing',
        income: 'Income',
    })[purpose];

export const getBudgetPurposeActionLabel = (purpose: BudgetPurpose) =>
    ({
        fixed: 'spent',
        lifestyle: 'spent',
        saving_investing: 'saved',
        income: 'earned',
    })[purpose];

export const getBudgetPurposeUnderLabel = (purpose: BudgetPurpose) =>
    ({
        fixed: 'under',
        lifestyle: 'under',
        saving_investing: 'to go',
        income: 'to go',
    })[purpose];

export const useBudgetedAmountForPurpose = ({
    budget,
    budgetPurpose,
    dateRange,
}: {
    budget: BudgetFieldsFragment;
    budgetPurpose: BudgetPurpose;
    dateRange?: DateRange;
}) => {
    const { userInfo } = useCurrentUser();

    const totalMonths = getTotalMonths(dateRange);

    switch (budgetPurpose) {
        case 'income':
            return userInfo.combinedOrIndividualMonthlyNetIncome * totalMonths;
        case 'lifestyle':
            return budget.lifestyleAmount * totalMonths;
        case 'fixed':
            return budget.fixedAmount * totalMonths;
        case 'saving_investing':
            return budget.savingInvestingAmount * totalMonths;
        default: {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const exhaustiveCheck: never = budgetPurpose;
            throw new CustomError('Unsupported budget purpose', {
                budgetPurpose,
            });
        }
    }
};

export const getTotalBudgetedAmount = ({
    budget,
    dateRange,
}: {
    budget: BudgetFieldsFragment;
    dateRange?: DateRange;
}) => {
    const totalBudgetedAmount =
        budget.lifestyleAmount +
        budget.fixedAmount +
        budget.savingInvestingAmount;
    return totalBudgetedAmount * getTotalMonths(dateRange);
};
